import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { DateField } from "@/components/ui/date-field";
import { MoneyInput } from "@/components/ui/money-input";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { PaymentsList } from "@/components/caixa/PaymentsList";
import { SaleItemsTable, type SaleItemsTableHandle } from "@/components/caixa/SaleItemsTable";
import { QuickAddClientModal } from "@/components/caixa/QuickAddClientModal";
import { PaymentOnboarding } from "@/components/caixa/PaymentOnboarding";
import { UserModal } from "@/components/users/UserModal";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import { logCrud } from "@/services/activities";

const saleSchema = z.object({
  client_id: z.string().uuid({ message: "Selecione um cliente" }),
  vendedor_id: z.string().uuid({ message: "Selecione um vendedor" }),
  desconto: z.number().min(0).default(0),
  data_entrega: z.string().optional(),
  descricao: z.string().optional(),
});

type SaleFormData = z.infer<typeof saleSchema>;

type SaleItem = {
  product_id: string;
  quantidade: number;
  preco_unitario: number;
  desconto: number;
  total: number;
};

type Payment = {
  data: string;
  bank_account_id: string;
  payment_method_id: string;
  valor: number;
  descricao?: string;
  status: "confirmado" | "pendente";
  data_prevista?: string;
  created_at?: string;
  confirmed_at?: string;
};

export default function PDV() {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();

  const form = useForm<SaleFormData>({
    resolver: zodResolver(saleSchema),
    defaultValues: {
      client_id: "",
      vendedor_id: "",
      desconto: 0,
      data_entrega: new Date().toISOString().split("T")[0],
      descricao: "",
    },
  });

  const [items, setItems] = useState<SaleItem[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [clientSelectOpen, setClientSelectOpen] = useState(false);
  const [sellerSelectOpen, setSellerSelectOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [productSelectOpen, setProductSelectOpen] = useState(false);
  const saleItemsRef = useRef<SaleItemsTableHandle | null>(null);

  const clientId = form.watch("client_id");

  const { data: settlementConfigs = [] } = useQuery({
    queryKey: ["settlement-configs", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from("payment_settlement_configs")
        .select("*")
        .eq("tenant_id", tenantId);
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId,
  });

  const { data: currentUser } = useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: bankAccounts = [] } = useQuery({
    queryKey: ["bank_accounts", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from("bank_accounts")
        .select("id")
        .eq("tenant_id", tenantId)
        .eq("status", "ativo");
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId,
  });

  const { data: clients } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const { data, error } = await supabase.from("clients").select("*").order("nome");
      if (error) throw error;
      return data;
    },
  });

  const { data: sellers } = useQuery({
    queryKey: ["sellers", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("tenant_id", tenantId)
        .order("nome_completo");
      if (error) throw error;
      return data;
    },
    enabled: !!tenantId,
  });

  useEffect(() => {
    setClientSelectOpen(true);
  }, []);

  useEffect(() => {
    if (currentUser?.id) {
      form.setValue("vendedor_id", currentUser.id, { shouldDirty: false });
    }
  }, [currentUser, form]);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    form.setValue("data_entrega", today, { shouldDirty: false });
  }, [form]);

  useEffect(() => {
    const hasClient = !!clientId;
    const hasProduct = items.length > 0 && items.some((item) => item.product_id);
    const noAccounts = (bankAccounts?.length || 0) === 0;
    // Wizard deve aparecer apenas quando não existem contas bancárias
    const shouldShow = hasClient && hasProduct && noAccounts;

    setShowOnboarding(shouldShow);
  }, [clientId, items, bankAccounts]);

  useEffect(() => {
    if (!productSelectOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.closest("[data-radix-popper-content-wrapper]") || target.closest("[data-radix-popover-content]"))) {
        return;
      }
      saleItemsRef.current?.closeProductSelect?.();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        saleItemsRef.current?.closeProductSelect?.();
      }
    };
    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [productSelectOpen]);

  const valorTotalItens = items.reduce((sum, item) => sum + item.total, 0);
  const desconto = Number(form.watch("desconto") || 0);
  const valorLiquido = valorTotalItens - desconto;
  const valorPago = payments.filter((p) => p.status === "confirmado").reduce((sum, p) => sum + p.valor, 0);
  const valorPendente = valorLiquido - valorPago;
  const canProceed = !!clientId && items.length > 0 && items.some((item) => item.product_id);

  const dismissProductSelect = () => {
    saleItemsRef.current?.closeProductSelect?.();
  };

  const computePaymentsToInsert = (transactionId: string) => {
    if (!tenantId) return [];
    const configMap = new Map<string, any>();
    settlementConfigs.forEach((config: any) => {
      const key = `${config.bank_account_id}-${config.payment_method_id}`;
      configMap.set(key, config);
    });

    return payments.map((p) => {
      const configKey = `${p.bank_account_id}-${p.payment_method_id}`;
      const settlementConfig = configMap.get(configKey);

      let feeAmount = 0;
      let netAmount = p.valor;
      let dataPrevista = p.data_prevista || p.data || new Date().toISOString().slice(0, 10);

      if (settlementConfig && p.status === "confirmado") {
        feeAmount = Number((p.valor * (settlementConfig.fee_percent / 100)).toFixed(2));
        netAmount = Number((p.valor - feeAmount).toFixed(2));

        if (!p.data_prevista) {
          const daysToAdd = settlementConfig.days_to_receive || 0;
          const baseDate = p.data ? new Date(p.data) : new Date();
          dataPrevista = new Date(baseDate.getTime() + daysToAdd * 24 * 60 * 60 * 1000)
            .toISOString()
            .slice(0, 10);
        }
      } else if (p.status === "pendente" && !p.data_prevista) {
        dataPrevista = p.data || new Date().toISOString().slice(0, 10);
      }

      return {
        transaction_id: transactionId,
        tenant_id: tenantId,
        bank_account_id: p.bank_account_id,
        payment_method_id: p.payment_method_id,
        valor: p.valor,
        status: p.status,
        data: p.data || null,
        descricao: p.descricao || null,
        data_prevista: dataPrevista,
        settlement_config_id: settlementConfig?.id || null,
        fee_amount: feeAmount,
        net_amount: netAmount,
        created_at: p.created_at ? new Date(p.created_at).toISOString() : undefined,
        confirmed_at: p.confirmed_at ? new Date(p.confirmed_at).toISOString() : undefined,
      };
    });
  };

  const createSaleMutation = useMutation({
    mutationFn: async (data: SaleFormData) => {
      if (!tenantId) throw new Error("Tenant não encontrado");

      const valorTotal = items.reduce((sum, item) => sum + item.total, 0);
      const valorLiquidoCalc = valorTotal - data.desconto;
      const valorPagoCalc = payments.filter((p) => p.status === "confirmado").reduce((sum, p) => sum + p.valor, 0);
      const status: "pendente" | "paga" = valorPagoCalc >= valorLiquidoCalc ? "paga" : "pendente";

      let openSessionId: string | null = null;
      const { data: openSession } = await supabase
        .from("cash_register_sessions")
        .select("id")
        .eq("tenant_id", tenantId)
        .eq("status", "open")
        .order("opened_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      openSessionId = openSession?.id ?? null;

      const transactionPayload: any = {
        tipo: "entrada" as const,
        client_id: data.client_id,
        vendedor_id: data.vendedor_id,
        valor_total: valorTotal,
        valor_liquido: valorLiquidoCalc,
        desconto: data.desconto,
        status,
        data_entrega: data.data_entrega || null,
        descricao: data.descricao || null,
        tenant_id: tenantId,
        cash_register_session_id: openSessionId,
      };

      const { data: transactionResult, error: transactionError } = await supabase
        .from("transactions")
        .insert([transactionPayload])
        .select()
        .single();

      if (transactionError) throw transactionError;

      const itemsToInsert = items.map((item) => ({
        transaction_id: transactionResult.id,
        product_id: item.product_id,
        quantidade: item.quantidade,
        preco_unitario: item.preco_unitario,
        total: item.total,
      }));

      if (itemsToInsert.length > 0) {
        const { error: itemsError } = await supabase.from("transaction_items").insert(itemsToInsert);
        if (itemsError) throw itemsError;
      }

      await supabase.from("transaction_payments").delete().eq("transaction_id", transactionResult.id);
      if (payments.length > 0) {
        const paymentsToInsert = computePaymentsToInsert(transactionResult.id);
        if (paymentsToInsert.length > 0) {
          const { error: paysErr } = await supabase.from("transaction_payments").insert(paymentsToInsert as any);
          if (paysErr) throw paysErr;
        }
      }

      return transactionResult;
    },
    onSuccess: (transactionResult) => {
      toast.success("Venda registrada com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["caixa-transactions"] });
      if (transactionResult?.id) {
        void logCrud({ entitySlug: "entity:transactions", operationSlug: "create", entityId: transactionResult.id, screenSlug: "pdv" });
      }
      handleClear();
    },
    onError: (error: Error) => {
      toast.error("Erro ao registrar venda: " + error.message);
    },
  });

  const onSubmit = (data: SaleFormData) => {
    if (items.length === 0 || !items.some((item) => item.product_id)) {
      toast.error("Adicione pelo menos um item à venda");
      return;
    }
    if (payments.length === 0) {
      toast.error(
        "Registre pelo menos um pagamento (pode ser pendente). Recomenda-se informar uma data prevista de pagamento."
      );
      return;
    }
    createSaleMutation.mutate(data);
  };

  const handleClear = () => {
    const today = new Date().toISOString().split("T")[0];
    form.reset({
      client_id: "",
      vendedor_id: currentUser?.id ?? "",
      desconto: 0,
      data_entrega: today,
      descricao: "",
    });
    setItems([]);
    setPayments([]);
    setShowOnboarding(false);
    setClientSelectOpen(true);
    requestAnimationFrame(() => {
      saleItemsRef.current?.openNewRowProductSelect();
    });
  };

  const isSubmitting = createSaleMutation.isPending;
  const hasPayments = payments.length > 0;

  return (
    <div className="flex flex-col h-full min-h-[calc(100vh-2rem)]">
      <div className="relative mt-2 md:mt-0 z-[1] md:sticky md:top-16 md:z-[10] bg-background border-b">
        <div className="max-w-7xl mx-auto px-1 md:px-6 py-1 md:py-3 flex items-center gap-3">
          <div className="flex-1">
            <h1 className="text-xl md:text-2xl font-semibold">Ponto de Venda • Registre suas vendas</h1>
            <p className="text-sm text-muted-foreground hidden sm:block">
              Registre vendas rapidamente com um layout otimizado para toque e desktop.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleClear} disabled={isSubmitting}>
              Limpar
            </Button>
            <Button
              size="sm"
              onClick={form.handleSubmit(onSubmit)}
              disabled={isSubmitting || !canProceed || !hasPayments}
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Registrar Venda
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto relative z-0">
        <div className="max-w-7xl mx-auto px-1 md:px-6 py-1 md:py-4 relative z-0">
          <div className="relative" data-caixa-dialog-content>
            <Form {...form}>
              <form className="space-y-2.5 md:space-y-3" onSubmit={form.handleSubmit(onSubmit)}>
                <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4 md:gap-3.5 lg:gap-5">
                  <FormField
                    control={form.control}
                    name="client_id"
                    render={({ field }) => (
                      <FormItem className="col-span-2 md:col-span-1">
                        <FormLabel className="sm:hidden">Cliente *</FormLabel>
                        <div className={`flex gap-2 ${clientSelectOpen ? "relative z-[5000]" : ""}`}>
                          <FormControl className="w-full">
                            <SearchableSelect
                              label={<span className="hidden sm:block">Cliente *</span>}
                              open={clientSelectOpen}
                              onOpenChange={setClientSelectOpen}
                              value={field.value}
                              onChange={(value) => {
                                field.onChange(value);
                                setClientSelectOpen(false);
                                requestAnimationFrame(() => {
                                  saleItemsRef.current?.openNewRowProductSelect();
                                });
                              }}
                              options={(clients ?? []).map((c: any) => ({ value: c.id, label: c.nome }))}
                              placeholder="Selecione o cliente"
                              withBackdrop={false}
                              renderAddModal={({ open, onOpenChange, onCreated }) => (
                                <QuickAddClientModal
                                  open={open}
                                  onOpenChange={onOpenChange}
                                  onClientCreated={async (clientId) => {
                                    await queryClient.invalidateQueries({ queryKey: ["clients"] });
                                    onCreated(clientId);
                                  }}
                                />
                              )}
                            />
                          </FormControl>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="vendedor_id"
                    render={({ field }) => (
                      <FormItem className="col-span-2 md:col-span-1">
                        <FormLabel className="sm:hidden">Vendedor *</FormLabel>
                        <div className={`${sellerSelectOpen ? "relative z-[5000]" : ""}`}>
                          <FormControl>
                            <SearchableSelect
                              label={<span className="hidden sm:block">Vendedor *</span>}
                              open={sellerSelectOpen}
                              onOpenChange={setSellerSelectOpen}
                              value={field.value}
                              onChange={field.onChange}
                              options={(sellers ?? []).map((s: any) => ({ value: s.id, label: s.nome_completo }))}
                              placeholder="Selecione o vendedor"
                              valueLabel={
                                (sellers ?? []).find((s: any) => s.id === field.value)?.nome_completo ||
                                (field.value === currentUser?.id
                                  ? (currentUser as any)?.user_metadata?.nome_completo
                                  : undefined)
                              }
                              withBackdrop={false}
                              renderAddModal={({ open, onOpenChange, onCreated }) => (
                                <UserModal
                                  isOpen={open}
                                  onClose={() => onOpenChange(false)}
                                  user={null}
                                  onCreated={({ id }) => {
                                    field.onChange(id);
                                    queryClient.invalidateQueries({ queryKey: ["sellers", tenantId] });
                                    onCreated(id);
                                  }}
                                />
                              )}
                            />
                          </FormControl>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="desconto"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Desconto</FormLabel>
                        <FormControl>
                          <MoneyInput
                            value={field.value ?? 0}
                            onValueChange={(value) => field.onChange(value)}
                            onBlur={field.onBlur}
                            placeholder="0,00"
                            disabled={isSubmitting}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="data_entrega"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Data de Entrega</FormLabel>
                        <FormControl>
                          <DateField {...field} disabled={isSubmitting} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-3.5 md:space-y-4">
                  <div className="-mt-1">
                    <SaleItemsTable
                      ref={saleItemsRef}
                      items={items}
                      onItemsChange={isSubmitting ? () => {} : setItems}
                      onProductSelectOpenChange={setProductSelectOpen}
                      readOnly={isSubmitting}
                    />
                  </div>

                  {canProceed && (
                    <>
                      {showOnboarding && (
                        <div className="mt-6" onMouseDownCapture={dismissProductSelect} onFocusCapture={dismissProductSelect}>
                          <PaymentOnboarding onComplete={() => setShowOnboarding(false)} />
                        </div>
                      )}

                      {!showOnboarding && (
                        <>
                          <div
                            className="grid gap-2.5 md:gap-3.5 md:grid-cols-2"
                            onMouseDownCapture={dismissProductSelect}
                            onFocusCapture={dismissProductSelect}
                          >
                            <div className="space-y-2.5 mt-0">
                              <div className="bg-muted/70 px-2.5 py-2 rounded-md border border-muted-foreground/10">
                                <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 text-sm">
                                  <div className="flex flex-col items-start text-md">
                                    <div className="text-left">
                                      <span className="text-muted-foreground inline-block mr-1.5">Subtotal:</span>
                                      <span>
                                        {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valorTotalItens)}
                                      </span>
                                    </div>
                                    {desconto > 0 && (
                                      <div>
                                        <span className="text-muted-foreground inline-block mr-1.5">Desconto:</span>
                                        <span className="font-semibold text-orange-300 inline-block">
                                          -
                                          {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(desconto)}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1 text-base md:text-lg font-semibold ml-auto">
                                    <span>Total</span>
                                    <span>
                                      {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valorLiquido)}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <PaymentsList
                                payments={payments}
                                onPaymentsChange={setPayments}
                                valorTotal={valorLiquido}
                                valorPago={valorPago}
                                valorPendente={valorPendente}
                                showSummary
                                showDetails={false}
                                readOnly={isSubmitting}
                              />
                            </div>

                            <div className="-mt-0.5">
                              <FormField
                                control={form.control}
                                name="descricao"
                                render={({ field }) => (
                                  <FormItem className="h-full space-y-1.5">
                                    <FormLabel>Observações adicionais do pedido:</FormLabel>
                                    <FormControl>
                                      <Textarea
                                        {...field}
                                        rows={3}
                                        className={desconto > 0 ? "min-h-[100px]" : "min-h-[84px]"}
                                        placeholder="Digite observações..."
                                        disabled={isSubmitting}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>

                          <div className="pt-0.5" onMouseDownCapture={dismissProductSelect} onFocusCapture={dismissProductSelect}>
                            <PaymentsList
                              payments={payments}
                              onPaymentsChange={isSubmitting ? () => {} : setPayments}
                              valorTotal={valorLiquido}
                              valorPago={valorPago}
                              valorPendente={valorPendente}
                              showSummary={false}
                              showDetails={true}
                              readOnly={isSubmitting}
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button type="button" variant="outline" onClick={handleClear} disabled={isSubmitting}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || !canProceed || !hasPayments}
                    onClick={(e) => {
                      if (!hasPayments) {
                        e.preventDefault();
                        toast.error(
                          "É necessário registrar pelo menos um pagamento. Você pode registrar como pendente e informar uma data prevista."
                        );
                      }
                    }}
                  >
                    {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    Registrar Venda
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}
